import React from 'react';
import PropTypes from 'prop-types';
import HomeSection from '../HomeSection';
import EditorialFriendCard from '../../../molecules/EditorialFriendCard';
import EditorialCardRail from '../../../molecules/EditorialCardRail';
import EditorialEmptyState from '../../../molecules/EditorialEmptyState';
import UserConnectionsModal from '../../../molecules/UserConnectionsModal';
import StartPostButton from '../../../atoms/StartPostButton';
import { ArrowRightIcon } from '../../../atoms/icons/EditorialProfileIcons';
import {
  resolveUserConnectionAvatarUrl,
  resolveUserConnectionHandle,
  resolveUserConnectionName,
  resolveUserConnectionRole,
  resolveUserConnectionUserId,
  resolveUserConnectionUsername,
} from '../../../../utils/userConnectionDisplay';
import './index.scss';

const VIEW_ALL_DEFAULT_LABEL = 'View All';

const connectionViewAllPropType = PropTypes.shape({
  label: PropTypes.node,
  ariaLabel: PropTypes.string,
  modalTitle: PropTypes.node,
  modalEyebrow: PropTypes.node,
  modalAriaLabel: PropTypes.string,
  closeLabel: PropTypes.string,
  loadPage: PropTypes.func,
  pageSize: PropTypes.number,
  disabled: PropTypes.bool,
  emptyTitle: PropTypes.node,
  emptyDescription: PropTypes.node,
  loadingTitle: PropTypes.node,
  errorTitle: PropTypes.node,
  errorDescription: PropTypes.node,
  retryLabel: PropTypes.node,
  loadMoreLabel: PropTypes.node,
  loadingMoreLabel: PropTypes.node,
});

export default function HomeFriendsSection({
  id = 'friends',
  eyebrow,
  title,
  side,
  showSide = false,
  friends = [],
  people,
  messageLabel,
  onMessageFriend,
  onSelectFriend,
  emptyTitle,
  emptyDescription,
  emptyActionLabel,
  onEmptyAction,
  inviteTitle,
  inviteDescription,
  inviteActionLabel,
  onInvite,
  showInviteRow = true,
  publicMedia = false,
  previousLabel = 'Scroll friends left',
  nextLabel = 'Scroll friends right',
  autoScrollDirection,
  autoScrollSpeed,
  additionalGroups = [],
  viewAll,
}) {
  const [activeConnectionModal, setActiveConnectionModal] = React.useState(null);
  const cards = Array.isArray(people) ? people : friends;
  const groups = Array.isArray(additionalGroups) ? additionalGroups.filter(Boolean) : [];

  const renderViewAllAction = ({
    config,
    groupCards,
    groupTitle,
    groupEyebrow = eyebrow,
    groupPublicMedia,
    groupOnSelectFriend,
  }) => {
    if (!config) {
      return null;
    }

    const handleOpen = () => {
      setActiveConnectionModal({
        ...config,
        title: config.modalTitle || groupTitle,
        eyebrow: config.modalEyebrow || groupEyebrow,
        initialItems: groupCards,
        publicMedia: groupPublicMedia,
        onSelectConnection: groupOnSelectFriend,
      });
    };

    return (
      <StartPostButton
        className="home-friends__view-all"
        label={config.label || VIEW_ALL_DEFAULT_LABEL}
        ariaLabel={config.ariaLabel}
        icon={<ArrowRightIcon width="16" height="16" />}
        onClick={handleOpen}
        disabled={config.disabled}
      />
    );
  };

  const renderConnectionRail = ({
    cards: groupCards,
    messageLabel: groupMessageLabel = messageLabel,
    onMessageFriend: groupOnMessageFriend = onMessageFriend,
    onSelectFriend: groupOnSelectFriend = onSelectFriend,
    emptyTitle: groupEmptyTitle = emptyTitle,
    emptyDescription: groupEmptyDescription = emptyDescription,
    emptyActionLabel: groupEmptyActionLabel = emptyActionLabel,
    onEmptyAction: groupOnEmptyAction = onEmptyAction,
    inviteTitle: groupInviteTitle = inviteTitle,
    inviteDescription: groupInviteDescription = inviteDescription,
    inviteActionLabel: groupInviteActionLabel = inviteActionLabel,
    onInvite: groupOnInvite = onInvite,
    showInviteRow: groupShowInviteRow = showInviteRow,
    publicMedia: groupPublicMedia = publicMedia,
    previousLabel: groupPreviousLabel = previousLabel,
    nextLabel: groupNextLabel = nextLabel,
    autoScrollDirection: groupAutoScrollDirection = autoScrollDirection,
    autoScrollSpeed: groupAutoScrollSpeed = autoScrollSpeed,
  }) => (
    groupCards.length > 0 ? (
      <EditorialCardRail
        rows={1}
        className="home-friends__rail"
        trackClassName="home-friends__grid"
        previousLabel={groupPreviousLabel}
        nextLabel={groupNextLabel}
        autoScrollDirection={groupAutoScrollDirection}
        autoScrollSpeed={groupAutoScrollSpeed}
      >
        {groupCards.map((friend, i) => {
          const friendUserId = resolveUserConnectionUserId(friend);
          const friendUsername = resolveUserConnectionUsername(friend);
          const handleMessageFriend = groupOnMessageFriend
            ? () => groupOnMessageFriend(friend)
            : undefined;
          const handleSelectFriend = groupOnSelectFriend && friendUserId
            ? () => groupOnSelectFriend(friend, friendUserId)
            : undefined;

          return (
            <EditorialFriendCard
              key={friend.id || friend.userId || i}
              userId={friendUserId || undefined}
              username={friendUsername || undefined}
              name={resolveUserConnectionName(friend)}
              role={resolveUserConnectionRole(friend)}
              handle={resolveUserConnectionHandle(friend)}
              avatarUrl={resolveUserConnectionAvatarUrl(friend, { publicMedia: groupPublicMedia })}
              publicMedia={groupPublicMedia}
              actionLabel={handleMessageFriend ? groupMessageLabel : null}
              onAction={handleMessageFriend}
              onSelect={handleSelectFriend}
            />
          );
        })}

        {groupShowInviteRow ? (
          <article className="home-friends__invite">
            <div className="home-friends__invite-title">{groupInviteTitle}</div>
            <div className="home-friends__invite-desc">{groupInviteDescription}</div>
            {groupOnInvite && groupInviteActionLabel ? (
              <div className="home-friends__invite-action">
                <button type="button" className="home-friends__invite-btn" onClick={groupOnInvite}>
                  {groupInviteActionLabel}
                </button>
              </div>
            ) : null}
          </article>
        ) : null}
      </EditorialCardRail>
    ) : (
      <EditorialEmptyState
        title={groupEmptyTitle}
        description={groupEmptyDescription}
        action={groupEmptyActionLabel ? (
          <button type="button" className="home-friends__invite-btn" onClick={groupOnEmptyAction}>
            {groupEmptyActionLabel}
          </button>
        ) : null}
      />
    )
  );

  const mainViewAllAction = renderViewAllAction({
    config: viewAll,
    groupCards: cards,
    groupTitle: title,
    groupPublicMedia: publicMedia,
    groupOnSelectFriend: onSelectFriend,
  });

  return (
    <HomeSection
      id={id}
      eyebrow={eyebrow}
      title={title}
      side={side}
      showSide={showSide}
      action={mainViewAllAction}
      className="home-friends"
    >
      <div className="home-friends__groups">
        <div className="home-friends__group">
          {renderConnectionRail({ cards })}
        </div>
        {groups.map((group, index) => {
          const groupCards = Array.isArray(group.people)
            ? group.people
            : (Array.isArray(group.friends) ? group.friends : []);
          const groupViewAllAction = renderViewAllAction({
            config: group.viewAll,
            groupCards,
            groupTitle: group.title,
            groupPublicMedia: group.publicMedia ?? publicMedia,
            groupOnSelectFriend: group.onSelectFriend || onSelectFriend,
          });
          return (
            <section className="home-friends__group" key={group.id || index}>
              {group.title || groupViewAllAction ? (
                <div className="home-friends__group-header">
                  {group.title ? (
                    <h3 className="home-friends__group-title">{group.title}</h3>
                  ) : null}
                  {groupViewAllAction ? (
                    <div className="home-friends__group-action">{groupViewAllAction}</div>
                  ) : null}
                </div>
              ) : null}
              {renderConnectionRail({
                cards: groupCards,
                messageLabel: group.messageLabel,
                onMessageFriend: group.onMessageFriend,
                onSelectFriend: group.onSelectFriend,
                emptyTitle: group.emptyTitle,
                emptyDescription: group.emptyDescription,
                emptyActionLabel: group.emptyActionLabel,
                onEmptyAction: group.onEmptyAction,
                inviteTitle: group.inviteTitle,
                inviteDescription: group.inviteDescription,
                inviteActionLabel: group.inviteActionLabel,
                onInvite: group.onInvite,
                showInviteRow: group.showInviteRow,
                publicMedia: group.publicMedia,
                previousLabel: group.previousLabel,
                nextLabel: group.nextLabel,
                autoScrollDirection: group.autoScrollDirection,
                autoScrollSpeed: group.autoScrollSpeed,
              })}
            </section>
          );
        })}
      </div>
      {activeConnectionModal ? (
        <UserConnectionsModal
          title={activeConnectionModal.title}
          eyebrow={activeConnectionModal.eyebrow}
          ariaLabel={activeConnectionModal.modalAriaLabel}
          closeLabel={activeConnectionModal.closeLabel}
          initialItems={activeConnectionModal.initialItems}
          loadPage={activeConnectionModal.loadPage}
          pageSize={activeConnectionModal.pageSize}
          publicMedia={activeConnectionModal.publicMedia}
          onSelectConnection={activeConnectionModal.onSelectConnection}
          onClose={() => setActiveConnectionModal(null)}
          emptyTitle={activeConnectionModal.emptyTitle}
          emptyDescription={activeConnectionModal.emptyDescription}
          loadingTitle={activeConnectionModal.loadingTitle}
          errorTitle={activeConnectionModal.errorTitle}
          errorDescription={activeConnectionModal.errorDescription}
          retryLabel={activeConnectionModal.retryLabel}
          loadMoreLabel={activeConnectionModal.loadMoreLabel}
          loadingMoreLabel={activeConnectionModal.loadingMoreLabel}
        />
      ) : null}
    </HomeSection>
  );
}

HomeFriendsSection.propTypes = {
  id: PropTypes.string,
  eyebrow: PropTypes.node.isRequired,
  title: PropTypes.node.isRequired,
  side: PropTypes.node,
  showSide: PropTypes.bool,
  friends: PropTypes.array,
  people: PropTypes.array,
  messageLabel: PropTypes.node,
  onMessageFriend: PropTypes.func,
  onSelectFriend: PropTypes.func,
  emptyTitle: PropTypes.node,
  emptyDescription: PropTypes.node,
  emptyActionLabel: PropTypes.node,
  onEmptyAction: PropTypes.func,
  inviteTitle: PropTypes.node,
  inviteDescription: PropTypes.node,
  inviteActionLabel: PropTypes.node,
  onInvite: PropTypes.func,
  showInviteRow: PropTypes.bool,
  publicMedia: PropTypes.bool,
  previousLabel: PropTypes.string,
  nextLabel: PropTypes.string,
  autoScrollDirection: PropTypes.oneOf(['left', 'right']),
  autoScrollSpeed: PropTypes.number,
  viewAll: connectionViewAllPropType,
  additionalGroups: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.node,
    friends: PropTypes.array,
    people: PropTypes.array,
    messageLabel: PropTypes.node,
    onMessageFriend: PropTypes.func,
    onSelectFriend: PropTypes.func,
    emptyTitle: PropTypes.node,
    emptyDescription: PropTypes.node,
    emptyActionLabel: PropTypes.node,
    onEmptyAction: PropTypes.func,
    inviteTitle: PropTypes.node,
    inviteDescription: PropTypes.node,
    inviteActionLabel: PropTypes.node,
    onInvite: PropTypes.func,
    showInviteRow: PropTypes.bool,
    publicMedia: PropTypes.bool,
    previousLabel: PropTypes.string,
    nextLabel: PropTypes.string,
    autoScrollDirection: PropTypes.oneOf(['left', 'right']),
    autoScrollSpeed: PropTypes.number,
    viewAll: connectionViewAllPropType,
  })),
};
